import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { IMAGES } from "@/lib/images";

const TOPICS = [
  "Trauma: The Root Issue",
  "From Survival to Strategy",
  "Leadership Behind the Wall",
  "Breaking Generational Cycles",
  "Mindset & Recidivism",
  "Staff-Facility Culture",
];

export default function SpeakingMediaSection() {
  return (
    <section id="speaking" className="relative py-28 lg:py-36 grain" data-testid="speaking-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-mono-tactical text-xs uppercase tracking-[0.25em] text-amber-500"
        >
          Speaking & Workshops
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-5 font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#F3F4F6] max-w-2xl"
          data-testid="speaking-heading"
        >
          Keynotes and in-facility workshops that residents actually hear.
        </motion.h2>

        <div className="mt-14 grid lg:grid-cols-12 gap-5">
          <motion.button
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            onClick={() => toast.info("The full keynote reel is shared with facilities on request — use the inquiry form below.")}
            className="lg:col-span-7 relative rounded-2xl overflow-hidden group text-left border border-white/10"
            data-testid="keynote-reel-card"
          >
            <img src={IMAGES.speakingWorkshop} alt="Angel Garcia leading a workshop" className="w-full aspect-video object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E11]/90 via-[#0D0E11]/20 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-amber-500 text-[#0D0E11] flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play size={24} fill="currentColor" />
              </span>
            </div>
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div>
                <div className="font-display font-bold text-xl text-[#F3F4F6]">Keynote Reel</div>
                <div className="font-mono-tactical text-[11px] tracking-[0.2em] text-amber-500 mt-1">S2S / IN-FACILITY</div>
              </div>
              <span className="font-mono-tactical text-xs text-[#9CA3AF]">02:47</span>
            </div>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 bg-[#151821] border border-white/10 rounded-2xl p-8"
            data-testid="keynote-topics-card"
          >
            <div className="font-mono-tactical text-xs tracking-[0.2em] text-[#6B7280]">SIGNATURE TOPICS</div>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {TOPICS.map((t, i) => (
                <span key={i} className="px-4 py-2 rounded-full border border-white/10 text-sm text-[#9CA3AF] hover:border-amber-500/40 hover:text-amber-500 transition-colors" data-testid={`topic-chip-${i}`}>
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-7 text-sm text-[#9CA3AF] leading-relaxed">
              Formats: 60-minute keynote, half-day staff workshop, or the full multi-week resident curriculum.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-6 relative rounded-2xl overflow-hidden border border-white/10"
            data-testid="gallery-boardroom"
          >
            <img src={IMAGES.boardroom} alt="Program presentation to facility leadership" className="w-full aspect-[16/9] object-cover grayscale-[30%] hover:grayscale-0 transition-all duration-700" />
            <div className="absolute bottom-4 left-5 font-mono-tactical text-[11px] tracking-[0.2em] text-[#F3F4F6]/80">ADMINISTRATION BRIEFINGS</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-6 relative rounded-2xl overflow-hidden border border-white/10"
            data-testid="gallery-audience"
          >
            <img src={IMAGES.audienceStage} alt="Audience at an S2S keynote" className="w-full aspect-[16/9] object-cover grayscale-[30%] hover:grayscale-0 transition-all duration-700" />
            <div className="absolute bottom-4 left-5 font-mono-tactical text-[11px] tracking-[0.2em] text-[#F3F4F6]/80">RESIDENT SESSIONS</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
